import type { ReactNode } from "react";
import { AlertTriangle, BookOpen, RefreshCw } from "lucide-react";
import { useIsFetching } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalization } from "@/context/localization-context";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock, ErrorBlock, LoadingBlock } from "@/monitoring/components/primitives";
import { errorMessage, useCatalog } from "./api";
import { IngredientsTab, ProductsTab, RecipesTab } from "./components/catalog-lists";
import { HistoryTab } from "./components/history-tab";
import { parseStamp } from "./model";

const TAB_STORAGE_KEY = "pizza-admin:catalog:tab:v1";
const TABS = ["recipes", "ingredients", "products", "history"] as const;
type Tab = (typeof TABS)[number];

/**
 * The developer page "Catalog": the customer's recipes, ingredients and products the evaluation checks
 * pizzas against, read from and edited in its database. A saved edit applies to the next evaluation.
 */
export function CatalogBoard() {
  const { t } = useLocalization();
  const catalogQuery = useCatalog();
  const fetchingAny = useIsFetching({ queryKey: ["catalog"] }) > 0;
  const [storedTab, setTab] = useLocalStorageState<string>(TAB_STORAGE_KEY, "recipes");
  const tab: Tab = (TABS as readonly string[]).includes(storedTab) ? (storedTab as Tab) : "recipes";

  const data = catalogQuery.data;
  const reload = () => catalogQuery.refetch();
  const empty = !!data && data.recipes.length === 0 && data.ingredients.length === 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 px-4 pt-4 lg:px-6 lg:pt-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <BookOpen className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">{t("catalog.title")}</h1>
          </div>
          {data?.last_import && <Pill>{data.last_import.name}</Pill>}
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => void reload()}
              title={t("catalog.reload")}
              aria-label={t("catalog.reload")}
            >
              <RefreshCw className={cn("h-4 w-4", fetchingAny && "animate-spin")} />
            </Button>
          </div>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("catalog.description")}</p>
        {data && (
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            {data.last_import ? (
              <>
                <span className="min-w-0 break-all">{t("catalog.lastImport", { who: data.last_import.by })}</span>
                <span aria-hidden>·</span>
                <Ago ts={parseStamp(data.last_import.at)} />
              </>
            ) : (
              t("catalog.neverImported")
            )}
          </p>
        )}
        {data?.source === "files" && (
          <div className="flex max-w-3xl items-start gap-2 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            <AlertTriangle className="mt-px h-4 w-4 shrink-0" aria-hidden />
            <span>{t("catalog.sourceFiles")}</span>
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-4 px-4 py-5 lg:px-6">
        {catalogQuery.error && !data && (
          <ErrorBlock message={errorMessage(catalogQuery.error)} onRetry={() => void reload()} />
        )}
        {!data && catalogQuery.isLoading && <LoadingBlock height={320} className="rounded-xl" />}
        {empty && <EmptyBlock>{t("catalog.empty")}</EmptyBlock>}

        {data && !empty && (
          <Tabs value={tab} onValueChange={setTab} className="flex min-w-0 flex-col gap-3">
            <div className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0">
              <TabsList className="h-10">
                <TabsTrigger value="recipes" className="h-8 gap-1.5">
                  {t("catalog.tab.recipes")}
                  <Count value={data.recipes.length} />
                </TabsTrigger>
                <TabsTrigger value="ingredients" className="h-8 gap-1.5">
                  {t("catalog.tab.ingredients")}
                  <Count value={data.ingredients.length} />
                </TabsTrigger>
                <TabsTrigger value="products" className="h-8 gap-1.5">
                  {t("catalog.tab.products")}
                  <Count value={data.products.length} />
                </TabsTrigger>
                <TabsTrigger value="history" className="h-8">
                  {t("catalog.tab.history")}
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="recipes" className="mt-0">
              <RecipesTab catalog={data} onReload={reload} />
            </TabsContent>
            <TabsContent value="ingredients" className="mt-0">
              <IngredientsTab catalog={data} onReload={reload} />
            </TabsContent>
            <TabsContent value="products" className="mt-0">
              <ProductsTab catalog={data} onReload={reload} />
            </TabsContent>
            <TabsContent value="history" className="mt-0">
              <HistoryTab catalog={data} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-md border px-2 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

/** a tab's count; phones leave it out so that the four tabs fit */
function Count({ value }: { value: number }) {
  return <span className="hidden rounded bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground sm:inline">{value}</span>;
}
